import { BaseTableRow, getHeaderName, TableProps, TableState } from "@/types";
import { Link } from "@mui/material";
import { DateLabel } from "klayowebshared";
import { SyntheticEvent } from "react";
import { Utils } from "../../common/Utils";
import { WithLocaleProps } from "../../providers/LocaleProvider";
import { Table } from "./Table";

interface BillingHistoryTableRow extends BaseTableRow {
  amountPaid: number;
  invoiceNumber: string;
  status: {
    label: string;
  };
  amount: number;
}

interface BillingHistoryTableProps
  extends TableProps<BillingHistoryTableRow>, Partial<WithLocaleProps> {
  rows: BillingHistoryTableRow[];
}

export class BillingHistoryTable extends Table<
  BillingHistoryTableRow,
  BillingHistoryTableProps,
  TableState
> {
  static columns = {
    date: {
      field: "date",
      label: "tableColumns.date"
    },
    invoice: {
      field: "invoice",
      label: "tableColumns.invoice"
    },
    billedTo: {
      field: "billedTo",
      label: "tableColumns.billedTo"
    },
    amount: {
      field: "amount",
      label: "tableColumns.amount"
    },
    paid: {
      field: "paid",
      label: "tableColumns.paid"
    },
    status: {
      field: "status",
      label: "tableColumns.status"
    }
  };

  static defaultProps = {
    ...Table.defaultProps,
    columns: [
      { type: BillingHistoryTable.columns.date },
      { type: BillingHistoryTable.columns.invoice },
      { type: BillingHistoryTable.columns.billedTo },
      { type: BillingHistoryTable.columns.amount },
      { type: BillingHistoryTable.columns.paid },
      { type: BillingHistoryTable.columns.status }
    ],
    noRowsMessage: "No rows",
    rowHasAction: false,
    selectable: true
  };

  constructor(props: BillingHistoryTableProps) {
    super(props);

    this.state = {
      ...this.state,
      sortModel: this.props.sortModel
        ? this.props.sortModel
        : [
            {
              field: "date",
              sort: "desc"
            }
          ]
    };
  }

  getRowId(a: BillingHistoryTableRow) {
    return this.tableId + "_" + a.invoiceId;
  }

  onClickDownloadLink(e: SyntheticEvent, invoice: BillingHistoryTableRow) {
    const { onClickDownloadLink } = this.props;
    if (onClickDownloadLink) onClickDownloadLink(e, invoice);
  }

  initColumns() {
    const { columns, sortable } = this.props;

    this.columns = [];

    if (!columns) return;

    columns.forEach((c) => {
      if (this.hasColumn(c)) {
        if (c.type === BillingHistoryTable.columns.date)
          this.columns.push({
            field: "date",
            headerName: getHeaderName(c, this.props.translate),
            sortable: sortable,
            width: 200,
            filterable: false,
            disableReorder: true,
            valueGetter: (_value, row) => row.date,
            renderCell: (params) => <DateLabel date={params.row.date} />
          });
        else if (c.type === BillingHistoryTable.columns.invoice)
          this.columns.push({
            field: "invoice",
            headerName: getHeaderName(c, this.props.translate),
            sortable: sortable,
            width: 240,
            filterable: false,
            disableReorder: true,
            valueGetter: (_value, row) => row.invoiceNumber,
            renderCell: (params) => (
              <Link
                className='klayo-billingtable_invoicelink'
                onClick={(e) => this.onClickDownloadLink(e, params.row)}
                target='_blank'
              >
                {params.row.invoiceNumber}
              </Link>
            )
          });
        else if (c.type === BillingHistoryTable.columns.amount)
          this.columns.push({
            field: "amount",
            headerName: getHeaderName(c, this.props.translate),
            sortable: sortable,
            width: 120,
            filterable: false,
            disableReorder: true,
            valueGetter: (_value, row) => "$" + Utils.formatCurrency(row.amount, 2)
          });
        else if (c.type === BillingHistoryTable.columns.paid)
          this.columns.push({
            field: "paid",
            headerName: getHeaderName(c, this.props.translate),
            sortable: sortable,
            width: 120,
            filterable: false,
            disableReorder: true,
            valueGetter: (_value, row) => "$" + Utils.formatCurrency(row.amountPaid, 2)
          });
        else if (c.type === BillingHistoryTable.columns.status)
          this.columns.push({
            field: "status",
            headerName: getHeaderName(c, this.props.translate),
            sortable: sortable,
            width: 150,
            filterable: false,
            disableReorder: true,
            valueGetter: (_value, row) => row.status.label
          });
      }
    });
  }
}
